<template>
  <div v-if="metaData" class="pagination-wrap">
    <p class="pagination-info">
      Showing
      <span class="value">{{ shownCount }}</span>
      of
      <span class="value">{{ metaData.totalRecords }}</span>
      results
    </p>

    <div class="pagination">
      <div class="custom-pagination" :class="{ 'tight-spacing': totalPages <= 10 }">
        <button
          type="button"
          class="control-prev"
          :disabled="currentPage === 1"
          aria-label="Previous page"
          v-on:click="prevPage"
        >
          <i class="pi pi-chevron-left"></i>
          Prev
        </button>
        <button
          v-for="(page, idx) in visiblePages"
          :key="`${page}-${idx}`"
          :class="[{ active: currentPage === page }, page === '...' ? 'ellipsis' : '']"
          :disabled="page === '...'"
          type="button"
          v-on:click="changePage(page)"
        >
          {{ page }}
        </button>
        <button
          type="button"
          class="control-next"
          :disabled="currentPage >= totalPages"
          aria-label="Next page"
          v-on:click="nextPage"
        >
          Next
          <i class="pi pi-chevron-right"></i>
        </button>
      </div>

      <div class="pagination-controls">
        <div class="per-page">
          <select class="per-page-select" :value="perPage" v-on:change="onPerPageChange">
            <option v-for="limit in perPageOptions" :key="limit" :value="limit">{{ limit }}</option>
          </select>
          <span class="per-page-label">per page</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "Pagination",
  props: {
    metaData: { type: Object, default: null },
    currentPage: { type: Number, required: true },
    perPage: { type: Number, required: true },
    perPageOptions: { type: Array, default: () => [30, 50, 100, 200] },
  },
  emits: ["update:currentPage", "update:perPage", "change"],
  computed: {
    totalPages() {
      return (this.metaData && this.metaData.totalPages) || 1;
    },
    shownCount() {
      if (!this.metaData) return 0;
      const upper = this.perPage * this.currentPage;
      return Math.min(upper, this.metaData.totalRecords);
    },
    visiblePages() {
      const total = this.totalPages;
      const current = this.currentPage;
      const pages = [];

      if (total <= 10) {
        for (let i = 1; i <= total; i++) pages.push(i);
        return pages;
      }

      let startPage = Math.max(1, current - 5);
      let endPage = Math.min(total, current + 5);

      if (current <= 6) {
        startPage = 1;
        endPage = 10;
      } else if (current >= total - 5) {
        startPage = total - 9;
        endPage = total;
      }

      for (let i = startPage; i <= endPage; i++) pages.push(i);
      if (endPage < total) pages.push("...");

      return pages;
    },
  },
  methods: {
    changePage(page) {
      if (page === "..." || page === this.currentPage) return;
      this.$emit("update:currentPage", page);
      this.$emit("change", { page, perPage: this.perPage });
    },
    prevPage() {
      if (this.currentPage > 1) {
        const page = this.currentPage - 1;
        this.$emit("update:currentPage", page);
        this.$emit("change", { page, perPage: this.perPage });
      }
    },
    nextPage() {
      if (this.currentPage < this.totalPages) {
        const page = this.currentPage + 1;
        this.$emit("update:currentPage", page);
        this.$emit("change", { page, perPage: this.perPage });
      }
    },
    onPerPageChange(event) {
      const next = Number(event.target.value);
      this.$emit("update:perPage", next);
      this.$emit("update:currentPage", 1);
      this.$emit("change", { page: 1, perPage: next });
    },
  },
};
</script>

<style scoped>
.pagination-wrap {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
}

.per-page-select {
  height: 33px!important;
}
</style>
